import React from "react";
import { Button, InputField } from "../atoms";
import Tabs from "./Tabs/Tabs";
import styled from "styled-components";
import { theme } from "../theme/semanticTheme";

const BAR_HEIGHT = 40;

const bp = theme.breakpoints || { md: "768px", sm: "640px" };

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  min-height: ${BAR_HEIGHT}px;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  @media (max-width: ${bp.md}) {
    justify-content: flex-start;
    padding: ${theme.spacing.md};
  }
  @media (max-width: ${bp.sm}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing.md};
  }
`;

const DateGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: nowrap;
  & > div {
    min-width: 140px;
  }
  @media (max-width: ${bp.sm}) {
    & > div {
      min-width: 0;
      flex: 1 1 0;
    }
  }
`;

const TabsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  height: ${BAR_HEIGHT}px;
  padding-left: ${theme.spacing.lg};
  border-left: 1px solid ${theme.colors.border};
  @media (max-width: ${bp.md}) {
    padding-left: ${theme.spacing.md};
  }
  @media (max-width: ${bp.sm}) {
    flex-wrap: wrap;
    height: auto;
    padding-left: 0;
    border-left: none;
    border-top: 1px solid ${theme.colors.border};
    padding-top: ${theme.spacing.md};
    justify-content: center;
  }
`;

const RangeErrorBanner = styled.div`
  flex-basis: 100%;
  margin-bottom: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  background: rgba(232, 44, 53, 0.06);
  border: 1px solid ${theme.colors.error};
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSize.sm};
`;

export default function DashboardFilterBar({
  periodOptions = [
    { value: null, label: "Today" },
    { value: "WEEK", label: "Week" },
    { value: "MONTH", label: "Month" },
    { value: "YEAR", label: "Year" },
    { value: "CUSTOM", label: "Custom" },
  ],
  selectedPeriod,
  onPeriodChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
}) {
  const showCustomDates = selectedPeriod === "CUSTOM";

  const fromVal = typeof startDate === "string" ? startDate : startDate ? startDate.toISOString?.()?.split("T")[0] : "";
  const toVal = typeof endDate === "string" ? endDate : endDate ? endDate.toISOString?.()?.split("T")[0] : "";

  let dateRangeError = "";
  if (showCustomDates && (fromVal || toVal)) {
    if ((fromVal && !toVal) || (!fromVal && toVal)) {
      dateRangeError = "Please select both From and To dates.";
    } else if (fromVal && toVal) {
      const fromDate = new Date(fromVal);
      const toDate = new Date(toVal);
      if (fromDate > toDate) {
        dateRangeError = "From Date cannot be after To Date.";
      }
    }
  }

  const disableApply = showCustomDates && (!fromVal || !toVal || !!dateRangeError);

  return (
    <Wrapper>
      {dateRangeError && <RangeErrorBanner>{dateRangeError}</RangeErrorBanner>}
      <TabsWrapper>
        <Tabs
          options={periodOptions}
          value={selectedPeriod}
          onChange={onPeriodChange}
          variant="segments"
          tabsOnly
        />
      </TabsWrapper>
      <RightGroup>
        {showCustomDates && (
          <DateGroup>
            <InputField
              name="startDate"
              type="date"
              title="From Date"
              value={fromVal}
              onChange={(val) => onStartDateChange(val ? new Date(val) : null)}
              max={new Date().toISOString().split("T")[0]}
            />
            <InputField
              name="endDate"
              type="date"
              title="To Date"
              value={toVal}
              onChange={(val) => onEndDateChange(val ? new Date(val) : null)}
              min={
                typeof startDate === "string"
                  ? startDate
                  : startDate
                    ? startDate.toISOString?.()?.split("T")[0]
                    : undefined
              }
              max={new Date().toISOString().split("T")[0]}
            />
          </DateGroup>
        )}
        {showCustomDates && (
          <Button variant="primary" onClick={onApply} disabled={disableApply}>
            Apply
          </Button>
        )}
      </RightGroup>
    </Wrapper>
  );
}
